import { useMemo } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import {
  useCreateTeamMemberMutation,
  useTeamCreateFieldsQuery,
} from '../api/useTeamQueries';
import { DynamicProfileFields } from '../components/DynamicProfileFields';
import {
  buildTeamCreatePayload,
  normalizeTeamCreateFields,
} from '../utils/teamUtils';

export default function AddTeamMemberPage() {
  const navigate = useNavigate();
  const mobileConfig = useMemo(() => getMobileConfig(), []);
  const createFieldsQ = useTeamCreateFieldsQuery();
  const createMut = useCreateTeamMemberMutation();

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<FieldValues>({ mode: 'onChange' });

  const selectedFields = useMemo(
    () => normalizeTeamCreateFields(createFieldsQ.data),
    [createFieldsQ.data],
  );

  const onSubmit = (data: FieldValues) => {
    const payload = buildTeamCreatePayload(data, mobileConfig.code);
    createMut.mutate(payload, {
      onSuccess: (res) => {
        const createdId =
          (res as { data?: { _id?: string } })?.data?._id ||
          (res as { data?: { id?: string } })?.data?.id;
        navigate(createdId ? `/team/${createdId}` : '/team');
      },
    });
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <button
        type="button"
        onClick={() => navigate('/team')}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--dash-muted)] transition hover:text-[var(--dash-ink)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to team
      </button>

      <header className="camp-fade-up text-left">
        <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
          Team
        </p>
        <h1 className="m-0 mt-0.5 text-xl font-semibold tracking-tight text-[var(--dash-ink)] sm:text-2xl">
          Add Team Member
        </h1>
        <p className="mt-0.5 mb-0 text-sm text-[var(--dash-muted)]">
          Create a team member with profile details only — no module permissions.
          Assign client dashboards after create.
        </p>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="camp-fade-up flex flex-col gap-3"
        style={{ animationDelay: '60ms' }}
      >
        <section className="camp-section">
          <header className="mb-5 border-b border-[var(--dash-line)] pb-4 text-left">
            <h2 className="m-0 text-base font-semibold tracking-tight text-[var(--dash-ink)]">
              Profile Identity
            </h2>
          </header>

          {createFieldsQ.isPending ? (
            <div className="camp-skeleton h-32 rounded-2xl" />
          ) : createFieldsQ.isError ? (
            <p className="m-0 text-sm text-red-600">
              Could not load create fields. Please try again.
            </p>
          ) : selectedFields.length === 0 ? (
            <p className="m-0 text-sm text-[var(--dash-muted)]">
              No create fields are configured for team members.
            </p>
          ) : (
            <DynamicProfileFields
              fields={selectedFields}
              register={register}
              control={control}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              mobileConfig={mobileConfig}
            />
          )}
        </section>

        <div className="sticky bottom-3 z-10 flex flex-col-reverse gap-3 rounded-2xl border border-[var(--dash-line)] bg-white/95 p-3 shadow-[0_12px_32px_-18px_rgba(16,38,73,0.45)] backdrop-blur sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate('/team')}
            className="h-11 rounded-xl px-5 text-sm font-semibold text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              !isValid ||
              createMut.isPending ||
              createFieldsQ.isPending ||
              createFieldsQ.isError ||
              selectedFields.length === 0
            }
            className="dash-btn-gradient inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white disabled:opacity-50"
          >
            {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create Team Member
          </button>
        </div>
      </form>
    </div>
  );
}
